import React, { useRef, useEffect, useState } from "react";
import "../styles/About.css";
import stalingunaImage from "../imagesebf/stalin.jpg";
import {
  FaCalendarAlt,
  FaBookOpen,
  FaEye,
  FaMicrophoneAlt,
  FaMapMarkerAlt,
  FaClock,
  FaUsers,
  FaLayerGroup,
  FaChartLine,
  FaQuoteLeft
} from "react-icons/fa";
import bookFestivalImage from "../imagesebf/mainlogo1.png";

/* ---------- Scroll-reveal wrapper (no external libs) ---------- */
const Reveal = ({ children, className = "", delay = 0, as: Tag = "div" }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (prefersReducedMotion) {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(node);
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -60px 0px" }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      className={`reveal ${visible ? "reveal-visible" : ""} ${className}`}
      style={{ transitionDelay: visible ? `${delay}ms` : "0ms" }}
    >
      {children}
    </Tag>
  );
};

const About = () => {
  const stats = [
    {
      icon: <FaCalendarAlt />,
      number: "20+",
      text: "வருடங்கள்"
    },
    {
      icon: <FaBookOpen />,
      number: "10 லட்சங்கள் +",
      text: "புத்தகங்கள்"
    },
    {
      icon: <FaEye />,
      number: "50 லட்சங்கள் +",
      text: "பார்வையாளர்கள்"
    },
    {
      icon: <FaMicrophoneAlt />,
      number: "400 +",
      text: "சொற்பொழிவாளர்"
    }
  ];

  const storyCards = [
    {
      icon: <FaBookOpen />,
      title: "வரலாறு மற்றும் வளர்ச்சி",
      content:
        "இந்த திருவிழா 2005 ஆம் ஆண்டில் தொடங்கப்பட்டது மற்றும் தமிழ்நாட்டில் சென்னைக்கு அடுத்தபடியாக இரண்டாவது பெரிய புத்தக திருவிழாவாக வளர்ந்துள்ளது..."
    },
    {
      icon: <FaUsers />,
      title: "ஏற்பாடு செய்பவர்கள் மற்றும் இடம்",
      content:
        "இது மக்கள் சிந்தனைப் பேரவை என்ற சமூக அமைப்பு மற்றும் மாவட்ட நிர்வாகத்தின் கூட்டு முயற்சியாகும்...",
      details: (
        <div className="story-detail-grid">
          <span>
            <FaMapMarkerAlt />
            சிக்கய்ய அரசு கலை மற்றும் அறிவியல் கல்லூரி மைதானம், ஈரோடு
          </span>
          <span>
            <FaCalendarAlt />
            ஜூலை 1 முதல் ஆகஸ்ட் 11 வரை
          </span>
          <span>
            <FaClock />
            காலை 11 முதல் இரவு 9.30 வரை
          </span>
          <span>
            <FaMicrophoneAlt />
            தினமும் மாலை 6 மணிக்கு அறிஞர்களின் சொற்பொழிவு
          </span>
        </div>
      )
    },
    {
      icon: <FaClock />,
      title: "கால அளவு மற்றும் நேரம்",
      content:
        "இந்த திருவிழா பொதுவாக ஆகஸ்ட் 4 முதல் 15 வரை 12 நாட்கள் நடைபெறுகிறது..."
    }
  ];

  return (
    <div className="about-container">
      {/* ============ HERO ============ */}
      <section className="about-hero">
        <div className="about-hero-grain"></div>
        <div className="about-hero-glow"></div>
        <Reveal as="div" className="about-hero-titleprnt">
          <span className="about-hero-badge">
            <FaBookOpen /> 2005 முதல்
          </span>
          <h1 className="about-hero-title">ஈரோடு புத்தகத் திருவிழா</h1>
          <p className="about-hero-subtitle">
            அறிவு, பண்பாடு மற்றும் இலக்கியத்தை பரப்பும் ஒரு இயக்கம்
          </p>
          <div className="about-hero-actions">
            <a href="/posts" className="card-button pastpostbtn">
              2025 நிகழ்வுகள்
            </a>
            <a href="#founder" className="card-button-ghost">
              நிறுவனரை அறிய
            </a>
          </div>
        </Reveal>
        <div className="about-hero-scroll-cue">
          <span></span>
        </div>
      </section>

      {/* ============ OUR STORY — sticky image + info cards ============ */}
      <section className="story-section">
        <Reveal as="div" className="section-header-abt">
          <span className="section-eyebrow-abt">எங்கள் பயணம்</span>
          <h2 className="about-section-title">எங்கள் வரலாறு</h2>
        </Reveal>

        <div className="story-layout">
          <Reveal as="div" className="story-sticky-image">
            <img
              src={bookFestivalImage}
              alt="ஈரோடு புத்தக திருவிழா"
              className="festival-image-abt"
            />
            <div className="story-image-caption">
              <strong>230+</strong>
              <span>கடைகள் ஒவ்வொரு ஆண்டும்</span>
            </div>
          </Reveal>

          <div className="story-cards-stack">
            {storyCards.map((card, index) => (
              <Reveal
                key={index}
                delay={index * 100}
                as="div"
                className="story-card"
              >
                <div className="story-card-icon">{card.icon}</div>
                <div className="story-card-body">
                  <h3>{card.title}</h3>
                  <p>{card.content}</p>
                  {card.details}
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* Features + Impact — pull-quote style pair */}
        <div className="story-highlight-pair">
          <Reveal as="div" className="highlight-card">
            <FaLayerGroup className="highlight-icon" />
            <h3>அளவு மற்றும் சிறப்பம்சங்கள்</h3>
            <p>
              ஈரோடு புத்தக திருவிழாவில் பல்வேறு வகைகள் மற்றும் பதிப்பகங்களின்
              தமிழ் மற்றும் ஆங்கிலத்தில் 230 கடைகள் உள்ளன...
            </p>
          </Reveal>
          <Reveal as="div" className="highlight-card" delay={120}>
            <FaChartLine className="highlight-icon" />
            <h3>தாக்கம் மற்றும் முக்கியத்துவம்</h3>
            <p>
              இந்த திருவிழா பொதுமக்கள் மற்றும் மாணவர்களிடையே புத்தக வாசிப்பு
              மற்றும் அறிவு வளர்ச்சியை ஊக்குவிப்பதை நோக்கமாகக் கொண்டுள்ளது...
            </p>
          </Reveal>
        </div>
      </section>

      {/* ============ FOUNDER — magazine spotlight ============ */}
      <section className="founder-section" id="founder">
        <Reveal as="div" className="section-header-abt">
          <span className="section-eyebrow-abt">நிறுவனர்</span>
          <h2 className="about-section-title">தலைமை மற்றும் தொலைநோக்கு</h2>
        </Reveal>

        <div className="founder-layout">
          <Reveal as="div" className="founder-photo-wrap">
            <div className="founder-photo-frame">
              <img
                src={stalingunaImage}
                alt="த. ஸ்டாலின் குணசேகரன்"
                className="founder-photo"
              />
            </div>
            <div className="founder-name-plate">
              <h3>த. ஸ்டாலின் குணசேகரன்</h3>
              <span>நிறுவனர் &amp; ஆசிரியர், மக்கள் சிந்தனை</span>
            </div>
          </Reveal>

          <Reveal as="div" className="founder-content" delay={150}>
            <FaQuoteLeft className="founder-quote-icon" />
            <p className="founder-text founder-text-lead">
              த. ஸ்டாலின் குணசேகரன் (பிறப்பு: ஜூலை 15, 1954) ஒரு தமிழ்
              எழுத்தாளர், பத்திரிகையாளர் மற்றும் சமூக ஆர்வலர். இவர் ஈரோடு
              புத்தகத் திருவிழாவின் மூலமாக தமிழ் இலக்கியத்திற்கு பெரும்
              பங்களிப்பு செய்துள்ளார்.
            </p>
            <p className="founder-text">
              "மக்கள் சிந்தனை" இதழின் ஆசிரியராக இருந்த இவர், பல சமூக
              இயக்கங்களில் ஈடுபட்டுள்ளார். இவரது எழுத்துக்கள் சமூக நீதி, மனித
              உரிமைகள் மற்றும் தமிழ் மொழி வளர்ச்சி பற்றியவை.
            </p>
            <div className="founder-tags">
              <span className="founder-tag">எழுத்தாளர்</span>
              <span className="founder-tag">பத்திரிகையாளர்</span>
              <span className="founder-tag">சமூக ஆர்வலர்</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ STATS BAND ============ */}
      <section className="about-stats-section">
        <Reveal as="div" className="section-header-abt light">
          <span className="section-eyebrow-abt light">எண்களில்</span>
          <h2 className="about-section-title light">
            எங்கள் திருவிழா எண்களில்
          </h2>
        </Reveal>
        <div className="about-stats-container">
          {stats.map((stat, index) => (
            <Reveal
              key={index}
              delay={index * 90}
              as="div"
              className="about-stat-item"
            >
              <div className="about-stat-icon">{stat.icon}</div>
              <h3 className="about-stat-number">{stat.number}</h3>
              <p className="about-stat-text">{stat.text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ============ CTA ============ */}
      <Reveal as="section" className="about-cta-section">
        <div className="about-cta-pattern"></div>
        <h2 className="about-cta-title">
          இந்த ஆண்டு புத்தகத் திருவிழாவில் பங்கேற்க விரும்புகிறீர்களா?
        </h2>
        <a className="about-cta-button" href="/contact">
          <span>எங்களை தொடர்பு கொள்ள</span>
        </a>
      </Reveal>
    </div>
  );
};

export default About;